import { chain, merge, omit, keyBy, pick, mapValues, isEmpty, isObject, isNil } from "lodash"
import { sanitizePositionSet, CUMULATIVE_RETURNS_SUBQUERY } from "../helpers"
import {
  updateGroupedAndTotalValues,
  groupFactorsByCategory,
  PERFORMANCE_NON_AGGREGABLE_KEYS,
  FACTOR_METADATA_SUBQUERY,
  getLatestData,
  createKeyedContributions,
} from "./helpers/factor-utils"
import { getResidualAttributionSummarySubquery, getResidualPeriodPerformanceSubquery } from "helpers/residualAttribution"

const PERFORMANCE_SUBQUERY = `
  annualizedReturns
  annualizedVolatility
  informationRatio
`

const FACTOR_PERFORMANCE_SUBQUERY = `
  factors {
    ${FACTOR_METADATA_SUBQUERY}
    performance (from: $from, to: $to, interval: END_DATE) {
      percentPriceChange1Day
      percentPriceChangeCumulative
      normalizedReturn
    }
  }
`

function buildFactorsPerformanceQuery(isRfrAttributionEnabled) {
  return `#factor-performance
    query ($modelId: String!, $positionSet: PositionSetInput!, $from: Date!, $to: Date!, $base: [PositionSetInput]) {
      model(id: $modelId) {
        ${FACTOR_PERFORMANCE_SUBQUERY}
        simulation(positionSet: $positionSet, from: $from, to: $to, base: $base, interval: END_DATE) {
          performance{
            date
            totalReturn {
              ${CUMULATIVE_RETURNS_SUBQUERY}
            }
            attribution{
              summary{
                factors {
                  ${CUMULATIVE_RETURNS_SUBQUERY}
                }
                specific {
                  ${CUMULATIVE_RETURNS_SUBQUERY}
                }
                trading {
                  ${CUMULATIVE_RETURNS_SUBQUERY}
                }
                ${isRfrAttributionEnabled ? getResidualAttributionSummarySubquery(true) : ""}
              }
              factors{
                ${FACTOR_METADATA_SUBQUERY}
                return {
                  ${CUMULATIVE_RETURNS_SUBQUERY}
                }
              }
            }
          }
          periodPerformance{
            ${PERFORMANCE_SUBQUERY}
            attribution{
              summary{
                factors{
                  ${PERFORMANCE_SUBQUERY}
                }
                specific {
                  ${PERFORMANCE_SUBQUERY}
                }
                ${isRfrAttributionEnabled ? getResidualPeriodPerformanceSubquery() : ""}
              }
              factors{
                ${FACTOR_METADATA_SUBQUERY}
                ${PERFORMANCE_SUBQUERY}
              }
            }
          }
        }
        exposureHistory: simulation(positionSet: $positionSet, from: $from, to: $to, base: $base, interval: AUTO) {
          exposure {
            factors {
              ${FACTOR_METADATA_SUBQUERY}
              currencyExposure
              net
            }
          }
        }
      }
    }`
}

export function mapFactorValuesToGroupAndTotal({ values: factorValues, factorsTotal, groupedFactors, factorCategory, key = null, parentKey = null }) {
  // We don't want to aggregate certain values at the category level
  if (PERFORMANCE_NON_AGGREGABLE_KEYS[key]) {
    return null
  }

  if (isObject(factorValues)) {
    return mapValues(factorValues, (value, factorKey) =>
      mapFactorValuesToGroupAndTotal({ values: value, factorsTotal, groupedFactors, factorCategory, key: factorKey, parentKey: key }),
    )
  }

  const processedValue = factorValues || 0

  if (!parentKey) {
    return processedValue
  }
  return updateGroupedAndTotalValues(processedValue, factorsTotal, groupedFactors, factorCategory, key, parentKey)
}

export function getGroupedAndTotalFactors(factors) {
  return groupFactorsByCategory(
    factors,
    PERFORMANCE_NON_AGGREGABLE_KEYS,
    true,
    mapFactorValuesToGroupAndTotal,
  )
}

function getFactorPerformance(factor) {
  if (!factor?.performance) {
    return {}
  }
  return pick(factor.performance[0], ["percentPriceChange1Day", "percentPriceChangeCumulative", "normalizedReturn"])
}

function getPeriodPerformance(performance) {
  if (!performance) {
    return {}
  }
  return pick(performance, ["annualizedReturns", "annualizedVolatility", "informationRatio"])
}

function getCumulativeReturn(performance, selectedMetricDisplay) {
  return { cumulativeReturn: performance?.[selectedMetricDisplay] }
}

function mergeFactorPerformance(performance = {}, periodPerformance = {}, factor = {}) {
  return chain(performance)
    .merge(periodPerformance, factor)
    .omit(["id", "name", "category"])
    .value()
}

function getMergedFactorPerformance(prefix, performance = [], periodPerformance = [], factors = [], selectedMetricDisplay) {
  return performance?.reduce((combinedFactors, performanceFactor) => {
    const factorId = performanceFactor.id
    const keyedPeriodPerformance = keyBy(periodPerformance, "id")
    const keyedFactors = keyBy(factors, "id")
    const periodPerformanceFactor = keyedPeriodPerformance[factorId]
    const factor = keyedFactors[factorId]

    combinedFactors[factorId] = {
      id: factorId,
      isFactor: true,
      label: performanceFactor.name,
      category: performanceFactor.category,
      values: {
        [prefix]: mergeFactorPerformance(
          getCumulativeReturn(performanceFactor.return, selectedMetricDisplay),
          getPeriodPerformance(periodPerformanceFactor),
          getFactorPerformance(factor)),
      },
    }
    return combinedFactors
  }, {})
}

function mergePerformanceValues(prefix, performance, periodPerformance, selectedMetricDisplay, factorsTotal = {}) {
  if (isEmpty(performance)) {
    return {}
  }
  return { [prefix]: merge(
    getCumulativeReturn(performance, selectedMetricDisplay),
    getPeriodPerformance(periodPerformance),
    factorsTotal,
  ) }
}

export function createPerformanceFactorsQuery({ prefix, modelId, positionSet, base, from, to, ...options }) {
  const sanitizedPositionSet = sanitizePositionSet(positionSet)

  // We see if we're running intraday queries elsewhere and if we are, then we
  // ignore the base
  const { shouldUseIntradayQuery, isRfrAttributionEnabled = false } = options

  const key = [
    "performance",
    "factors",
    prefix,
    modelId,
    positionSet,
    from,
    to,
    shouldUseIntradayQuery,
    isRfrAttributionEnabled,
    ...(shouldUseIntradayQuery ? [] : [base]),
  ]

  return {
    prefix,
    key,
    query: buildFactorsPerformanceQuery(isRfrAttributionEnabled),
    variables: {
      from,
      to,
      base: shouldUseIntradayQuery ? null : base,
      modelId,
      positionSet: sanitizedPositionSet,
    },
    select: (result) => {
      const factors = result.data.model.factors
      const periodPerformance = result.data.model.simulation.periodPerformance
      const periodPerformanceAttribution = periodPerformance?.attribution || {}
      const performance = result.data.model.simulation.performance[0]
      const performanceAttribution = performance?.attribution || {}
      const baseChildren = getMergedFactorPerformance(prefix, performanceAttribution.factors, periodPerformanceAttribution.factors, factors, options.selectedMetricDisplay)

      const exposureHistory = result.data.model.exposureHistory?.exposure || []
      const latestExposureFactors = getLatestData(exposureHistory, "factors") || []
      const keyedExposureFactors = keyBy(latestExposureFactors, "id")
      const netExposureContributions = createKeyedContributions(exposureHistory, "factors", "net")

      const children = Object.entries(baseChildren).reduce((acc, [childId, childFactor]) => {
        const exposureEntry = keyedExposureFactors[childId]
        const factorNetExposure = exposureEntry?.net
        const factorNetExposureContribution = netExposureContributions.map((dateEntry) => dateEntry?.[childId] || { value: undefined })

        acc[childId] = {
          ...childFactor,
          values: {
            ...childFactor.values,
            [prefix]: {
              ...(childFactor.values[prefix] || {}),
              netExposure: factorNetExposure,
              netExposureContribution: factorNetExposureContribution,
            },
          },
        }

        return acc
      }, {})

      const { groupedFactors, factorsTotal } = getGroupedAndTotalFactors(children)
      const hasTrading = !!performanceAttribution.summary?.trading?.cumulativePercent
      const hasResidual = isRfrAttributionEnabled && !isNil(performanceAttribution.summary?.residual)
      return {
        label: "Total",
        values: mergePerformanceValues(prefix, performance?.totalReturn, omit(periodPerformance, "attribution"), options.selectedMetricDisplay, factorsTotal),
        children: {
          Specific: {
            label: "Specific",
            values: mergePerformanceValues(prefix, performanceAttribution.summary?.specific, periodPerformanceAttribution.summary?.specific, options.selectedMetricDisplay),
            pinnedRowOrder: 4,
          },
          ...hasTrading && { Trading: {
            label: "Trading",
            values: mergePerformanceValues(prefix, performanceAttribution.summary?.trading, periodPerformanceAttribution.summary?.trading, options.selectedMetricDisplay, factorsTotal),
            pinnedRowOrder: 1,
          } },
          Factors: {
            label: "Factors",
            values: mergePerformanceValues(prefix, performanceAttribution.summary?.factors, periodPerformanceAttribution.summary?.factors, options.selectedMetricDisplay, factorsTotal),
            children,
            pinnedRowOrder: 3,
          },
          ...hasResidual && { Residual: {
            label: "Residual",
            values: mergePerformanceValues(prefix, performanceAttribution.summary?.residual, periodPerformanceAttribution.summary?.residual, options.selectedMetricDisplay),
            pinnedRowOrder: 2,
          } },
          ...groupedFactors,
        },
      }
    },
  }
}
